import { EditorStarterItem } from '../item-type';
import { computeGroupGeometry } from './group-geometry';

/**
 * GroupItem 의 시간 축 불변식을 복구하는 순수 후처리.
 *
 * GroupItem 은 from/durationInFrames 를 SoT 로 저장하지 않는다 — 렌더·선택은 children union
 * (computeGroupGeometry)을 쓰고, 아이템에 저장된 값은 타임라인 막대용 캐시다. 그래서 타임라인에서
 * 그룹을 드래그·트림하면 그룹 아이템의 시간 필드만 바뀌고 children 은 제자리에 남아 화면이 안 바뀐다
 * (좌표축에서 translateGroupChildren 이 푸는 것과 같은 문제).
 *
 * 시간 write 경로가 여럿(드래그 커밋·트림·마그네틱 push·리플 등)이라 setState 후처리에서 한 번에
 * 잡는다:
 * - 그룹 시간만 바뀜(children 불변) → children 에 전파. 오프셋·길이를 새 구간으로 스케일하므로
 *   이동(scale=1 → 단순 시프트)·좌트림·우트림이 한 식으로 처리된다. 양 끝 멤버는 새 경계에 스냅해
 *   children union == 그룹 구간을 정확히 유지한다(안 맞으면 다음 렌더에 막대가 되돌아간다).
 * - children 이 바뀜(멤버 개별 편집·undo·bake 로드) → children 이 진실이므로 그룹 캐시를 재계산.
 */
export const syncGroupChildrenTiming = (
  prevItems: Record<string, EditorStarterItem>,
  nextItems: Record<string, EditorStarterItem>,
): Record<string, EditorStarterItem> => {
  // items 를 안 건드리는 setState(선택 변경·스냅 갱신 등)가 절대다수라 스캔 자체를 건너뛴다.
  if (prevItems === nextItems) return nextItems;

  let result = nextItems;
  const write = (id: string, item: EditorStarterItem): void => {
    if (result === nextItems) result = { ...nextItems };
    result[id] = item;
  };

  for (const item of Object.values(nextItems)) {
    if (item.type !== 'group' || item.children.length === 0) continue;
    // 길이 0 이하 그룹은 스케일 상한(durationInFrames - 1)이 음수가 돼 멤버가 그룹 시작보다
    // 앞으로 밀린다. 현재 트림 경로는 duration ≥ 1 을 강제하지만 여기서도 막는다.
    if (item.durationInFrames <= 0) continue;

    const prevGroup = prevItems[item.id];
    // 멤버 목록은 내용으로 비교한다 — 배열을 새로 만들며 시간만 바꾸는 writer 가 있어
    // 참조 비교로는 "멤버 변경"으로 오판해 전파 대신 캐시 재계산으로 새어나간다.
    const membershipChanged =
      prevGroup?.type !== 'group' ||
      prevGroup.children.length !== item.children.length ||
      item.children.some((id, index) => prevGroup.children[index] !== id);
    const childrenChanged =
      membershipChanged ||
      item.children.some((id) => prevItems[id] !== nextItems[id]);

    // A cut compound owns its viewport and interval. Child edits must not
    // collapse padding or rescale media. A pure move still moves the family.
    if (item.preserveGroupFrame) {
      if (!childrenChanged && prevGroup?.type === 'group' &&
          prevGroup.preserveGroupFrame &&
          item.durationInFrames === prevGroup.durationInFrames &&
          item.from !== prevGroup.from) {
        const delta = item.from - prevGroup.from;
        const seen = new Set([item.id]);
        const shift = (id: string): void => {
          if (seen.has(id)) return;
          seen.add(id);
          const child = result[id];
          if (!child) return;
          write(id, {...child, from: child.from + delta});
          if (child.type === 'group') child.children.forEach(shift);
        };
        item.children.forEach(shift);
      }
      continue;
    }

    if (childrenChanged || (prevGroup?.type === 'group' && prevGroup.preserveGroupFrame)) {
      // children 이 진실 — 그룹의 시간 캐시를 union 으로 맞춘다.
      const geometry = computeGroupGeometry(item.children, nextItems);
      if (
        geometry.durationInFrames > 0 &&
        (geometry.from !== item.from ||
          geometry.durationInFrames !== item.durationInFrames)
      ) {
        write(item.id, {
          ...item,
          from: geometry.from,
          durationInFrames: geometry.durationInFrames,
        });
      }
      continue;
    }

    // children 이 그대로면 prevItems 로 잰 union 이 곧 "이 업데이트 전" 그룹 구간이다.
    const prevGeometry = computeGroupGeometry(item.children, prevItems);
    if (prevGeometry.durationInFrames <= 0) continue;
    if (
      prevGeometry.from === item.from &&
      prevGeometry.durationInFrames === item.durationInFrames
    ) {
      continue; // 시간 변화 없음
    }

    const scale = item.durationInFrames / prevGeometry.durationInFrames;
    for (const childId of item.children) {
      const child = nextItems[childId];
      if (!child) continue;

      const offset = child.from - prevGeometry.from;
      const end = offset + child.durationInFrames;
      // 양 끝 멤버는 반올림 오차 없이 새 경계에 붙인다(union == 그룹 구간 보장).
      const newOffset =
        offset === 0
          ? 0
          : Math.min(Math.round(offset * scale), item.durationInFrames - 1);
      const newEnd =
        end === prevGeometry.durationInFrames
          ? item.durationInFrames
          : Math.min(
              Math.max(Math.round(end * scale), newOffset + 1),
              item.durationInFrames,
            );

      write(childId, {
        ...child,
        from: item.from + newOffset,
        durationInFrames: newEnd - newOffset,
      });
    }
  }

  return result;
};
